import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

function RedeemDialog({ perk, onClose }) {
  const [tok, setTok] = useState(null);
  const [status, setStatus] = useState("initiated");
  const timer = useRef(null);
  const start = () => api.post(`/my/perks/${perk.perk_id}/redeem`).then((r) => { setTok(r.data); setStatus("initiated"); })
    .catch((e) => { toast.error(String(e.response?.data?.detail || "Not available").replaceAll("_", " ")); onClose(); });
  useEffect(() => { start(); return () => clearInterval(timer.current); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!tok) return undefined;
    timer.current = setInterval(() => api.get(`/my/perks/redemptions/${tok.redemption_id}`).then((r) => setStatus(r.data.status)).catch(() => {}), 2500);
    return () => clearInterval(timer.current);
  }, [tok]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm text-center" data-testid="perk-redeem-dialog">
        <DialogHeader><DialogTitle>{perk.title}</DialogTitle></DialogHeader>
        {status === "confirmed" ? <div className="py-6 text-emerald-600 font-bold text-xl flex flex-col items-center gap-2" data-testid="perk-redeemed"><CheckCircle2 size={48} /> Redeemed ✓</div>
          : tok ? <div className="space-y-3 flex flex-col items-center">
            <QRCodeSVG value={tok.qr_token} size={200} data-testid="perk-qr" />
            <div className="text-xs text-slate-500">Show this to staff. It expires in 2 minutes and works once.</div>
            <div className="text-sm">Can't scan? Code: <strong className="tracking-widest text-lg" data-testid="perk-manual-code">{tok.manual_code}</strong></div>
            <Button size="sm" variant="outline" onClick={start} data-testid="perk-refresh-code">New code</Button>
          </div> : <p className="text-slate-400">Preparing…</p>}
      </DialogContent>
    </Dialog>
  );
}

export function PerksTab() {
  const [perks, setPerks] = useState([]);
  const [open, setOpen] = useState(null);
  const [redeem, setRedeem] = useState(null);
  useEffect(() => { api.get("/my/perks").then((r) => setPerks(r.data)).catch(() => setPerks([])); }, []);
  const view = (p) => { setOpen(p.perk_id === open ? null : p.perk_id); if (p.perk_id !== open) api.post(`/my/perks/${p.perk_id}/open`).catch(() => {}); };
  return (
    <div className="space-y-2" data-testid="my-perks">
      {perks.length === 0 && <p className="text-sm text-slate-400">No perks available right now.</p>}
      {perks.map((p) => (
        <div key={p.perk_id} className="rounded-2xl border bg-white p-4" data-testid={`my-perk-${p.perk_id}`}>
          <button className="w-full text-left" onClick={() => view(p)} data-testid={`my-perk-open-${p.perk_id}`}>
            <div className="font-semibold text-slate-800">{p.title}</div>
            <div className="text-xs text-slate-500">{p.provided_by_caterctrl ? "CaterCTRL Perks" : `Offered by ${p.provider} — not provided or guaranteed by CaterCTRL`}</div>
          </button>
          {open === p.perk_id && <div className="mt-2 text-sm space-y-2"><p>{p.description}</p>{p.terms && <p className="text-xs text-slate-500">Terms: {p.terms}</p>}
            <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => setRedeem(p)} data-testid={`my-perk-redeem-${p.perk_id}`}>Redeem</Button></div>}
        </div>))}
      {redeem && <RedeemDialog perk={redeem} onClose={() => setRedeem(null)} />}
    </div>
  );
}
